import React, { useState, useEffect, useCallback, useRef } from 'react';
import { exchangeRatesApi } from '../services/api';

const CURRENCY_SYMBOL = { EUR: '€', USD: '$', GBP: '£' };
const CURRENCY_NAMES = { EUR: 'Евро', USD: 'Доллар США', GBP: 'Фунт стерлингов' };

/**
 * Виджет курсов валют (EUR, USD, GBP).
 * compact=true — миниатюрный столбик в шапке с попапом для редактирования.
 * compact=false — полная таблица для страницы настроек.
 */
export default function ExchangeRatesWidget({ compact = false }) {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const popupRef = useRef(null);
  const triggerRef = useRef(null);

  const loadRates = useCallback(async () => {
    try {
      setError(null);
      const res = await exchangeRatesApi.getAll();
      setRates(res.data);
    } catch (e) {
      setError('—');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRates(); }, [loadRates]);

  // Закрывать попап при клике вне
  useEffect(() => {
    if (!popupOpen) return;
    const handler = (e) => {
      if (
        popupRef.current && !popupRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setPopupOpen(false);
        setEditingCurrency(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popupOpen]);

  const handleEdit = (rate) => {
    setEditingCurrency(rate.currency);
    setEditValue(String(rate.rate));
  };

  const handleSave = async (currency) => {
    const val = parseFloat(editValue.replace(',', '.'));
    if (isNaN(val) || val <= 0) return;
    setSaving(true);
    try {
      await exchangeRatesApi.update(currency, val, true);
      setEditingCurrency(null);
      await loadRates();
    } catch (e) {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleResetManual = async (currency) => {
    try {
      await exchangeRatesApi.resetManual(currency);
      await loadRates();
    } catch (e) { /* ignore */ }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await exchangeRatesApi.refresh();
      setTimeout(async () => { await loadRates(); setRefreshing(false); }, 2500);
    } catch (e) { setRefreshing(false); }
  };

  // ─── Компактный режим: столбик в шапке ───────────────────────────────────
  if (compact) {
    return (
      <div style={{ position: 'relative' }}>
        {/* Триггер — столбик курсов */}
        <div
          ref={triggerRef}
          onClick={() => { setPopupOpen(v => !v); setEditingCurrency(null); }}
          title="Курсы валют — нажмите для редактирования"
          style={{
            cursor: 'pointer',
            lineHeight: 1.35,
            fontSize: 11,
            color: '#9ca3af',
            userSelect: 'none',
            padding: '2px 6px',
            borderRadius: 4,
            border: '1px solid transparent',
            transition: 'border-color 0.15s',
            ...(popupOpen ? { borderColor: '#d1d5db', background: '#f9fafb' } : {}),
          }}
          onMouseEnter={e => { if (!popupOpen) e.currentTarget.style.borderColor = '#e5e7eb'; }}
          onMouseLeave={e => { if (!popupOpen) e.currentTarget.style.borderColor = 'transparent'; }}
        >
          {loading ? (
            <span>💱</span>
          ) : error ? (
            <span style={{ color: '#ef4444' }}>курсы</span>
          ) : (
            rates.map(r => (
              <div key={r.currency} style={{ display: 'flex', justifyContent: 'space-between', gap: 6, whiteSpace: 'nowrap' }}>
                <span style={{ fontWeight: 500, color: r.is_manual ? '#d97706' : '#6b7280' }}>
                  {CURRENCY_SYMBOL[r.currency]}
                </span>
                <span style={{ color: r.is_manual ? '#d97706' : '#9ca3af' }}>
                  {r.rate.toLocaleString('ru', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            ))
          )}
        </div>

        {/* Попап редактирования */}
        {popupOpen && (
          <div
            ref={popupRef}
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              width: 280,
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              zIndex: 9999,
              overflow: 'hidden',
            }}
          >
            {/* Заголовок попапа */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px 8px', borderBottom: '1px solid #f3f4f6' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>💱 Курсы валют</span>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#6b7280', padding: '2px 4px' }}
                title="Обновить с ЦБ РФ"
              >
                {refreshing ? '⏳' : '🔄 ЦБ РФ'}
              </button>
            </div>

            {/* Список курсов */}
            {rates.map(r => (
              <div key={r.currency} style={{ padding: '8px 14px', borderBottom: '1px solid #f9fafb' }}>
                {editingCurrency === r.currency ? (
                  /* Режим редактирования */
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 28, fontSize: 15, fontWeight: 700 }}>{CURRENCY_SYMBOL[r.currency]}</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSave(r.currency); if (e.key === 'Escape') setEditingCurrency(null); }}
                      style={{ flex: 1, padding: '3px 6px', border: '1px solid #3b82f6', borderRadius: 4, fontSize: 13, textAlign: 'right' }}
                      autoFocus
                    />
                    <span style={{ fontSize: 12, color: '#6b7280' }}>₽</span>
                    <button
                      onClick={() => handleSave(r.currency)}
                      disabled={saving}
                      style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}
                    >✓</button>
                    <button
                      onClick={() => setEditingCurrency(null)}
                      style={{ background: '#6b7280', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}
                    >✕</button>
                  </div>
                ) : (
                  /* Режим просмотра */
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 28, fontSize: 15, fontWeight: 700 }}>{CURRENCY_SYMBOL[r.currency]}</span>
                    <span style={{ fontSize: 11, color: '#9ca3af', flex: 1 }}>{CURRENCY_NAMES[r.currency]}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: r.is_manual ? '#d97706' : '#111827' }}>
                      {r.rate.toLocaleString('ru', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
                    </span>
                    {r.is_manual && (
                      <span
                        title="Ручной курс — нажмите для сброса"
                        onClick={() => handleResetManual(r.currency)}
                        style={{ fontSize: 10, color: '#d97706', cursor: 'pointer', marginLeft: 2 }}
                      >✏️</span>
                    )}
                    <button
                      onClick={() => handleEdit(r)}
                      style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 7px', fontSize: 11, cursor: 'pointer', color: '#374151', marginLeft: 2 }}
                    >
                      Изменить
                    </button>
                  </div>
                )}
              </div>
            ))}

            <div style={{ padding: '6px 14px 8px', fontSize: 10, color: '#9ca3af' }}>
              Автообновление с ЦБ РФ каждое утро в 06:00. ✏️ = ручной курс.
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Полный режим ─────────────────────────────────────────────────────────
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, maxWidth: 480 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>💱 Курсы валют</h3>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer', color: '#374151' }}
        >
          {refreshing ? '⏳ Обновление...' : '🔄 Обновить с ЦБ РФ'}
        </button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
            <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 500 }}>Валюта</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b7280', fontWeight: 500 }}>Курс (₽)</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', color: '#6b7280', fontWeight: 500 }}>Источник</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b7280', fontWeight: 500 }}>Обновлено</th>
            <th style={{ width: 100 }}></th>
          </tr>
        </thead>
        <tbody>
          {rates.map(r => (
            <tr key={r.currency} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '10px 8px', fontWeight: 600 }}>
                {CURRENCY_SYMBOL[r.currency]} {r.currency}
                <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12, marginLeft: 6 }}>{CURRENCY_NAMES[r.currency]}</span>
              </td>
              <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                {editingCurrency === r.currency ? (
                  <input
                    type="number" step="0.01" min="0.01"
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSave(r.currency); if (e.key === 'Escape') setEditingCurrency(null); }}
                    style={{ width: 90, padding: '3px 6px', border: '1px solid #d97706', borderRadius: 4, fontSize: 14, textAlign: 'right' }}
                    autoFocus
                  />
                ) : (
                  <span style={{ fontWeight: 500 }}>
                    {r.rate.toLocaleString('ru', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                  </span>
                )}
              </td>
              <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                {r.is_manual ? (
                  <span
                    style={{ background: '#fef3c7', color: '#d97706', borderRadius: 4, padding: '2px 8px', fontSize: 12, cursor: 'pointer' }}
                    onClick={() => handleResetManual(r.currency)}
                    title="Нажмите чтобы сбросить и использовать ЦБ РФ"
                  >✏️ Вручную</span>
                ) : (
                  <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>🏦 ЦБ РФ</span>
                )}
              </td>
              <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: 12, color: '#9ca3af' }}>
                {r.updated_at ? new Date(r.updated_at).toLocaleString('ru', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
              </td>
              <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                {editingCurrency === r.currency ? (
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <button onClick={() => handleSave(r.currency)} disabled={saving} style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>✓</button>
                    <button onClick={() => setEditingCurrency(null)} style={{ background: '#6b7280', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>✕</button>
                  </div>
                ) : (
                  <button onClick={() => handleEdit(r)} style={{ background: 'transparent', border: '1px solid #d1d5db', borderRadius: 4, padding: '3px 10px', fontSize: 12, cursor: 'pointer', color: '#374151' }}>
                    Изменить
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ margin: '12px 0 0', fontSize: 12, color: '#9ca3af' }}>
        Курсы обновляются автоматически каждое утро в 06:00 с сайта ЦБ РФ. Ручной курс не будет перезаписан автоматически.
      </p>
    </div>
  );
}
